import { Injectable, signal } from '@angular/core';

export type CookieConsent = 'accepted' | 'rejected' | null;

@Injectable({
  providedIn: 'root'
})
export class CookieConsentService {
  private readonly STORAGE_KEY = 'cookie-consent';
  
  // Signal for consent state
  consent = signal<CookieConsent>(this.getStoredConsent());

  constructor() {
    // Check if consent was already given
    this.consent.set(this.getStoredConsent());
  }

  /**
   * Get stored consent from localStorage
   */
  private getStoredConsent(): CookieConsent {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored === 'accepted' || stored === 'rejected') {
        return stored;
      }
      return null;
    } catch {
      return null;
    }
  }

  /**
   * Accept all cookies
   */
  acceptAll(): void {
    this.setConsent('accepted');
  }

  /**
   * Reject cookies
   */
  reject(): void {
    this.setConsent('rejected');
  }

  /**
   * Set consent and save to localStorage
   */
  private setConsent(value: CookieConsent): void {
    this.consent.set(value);
    if (value) {
      try {
        localStorage.setItem(this.STORAGE_KEY, value);
      } catch (error) {
        console.error('Failed to save cookie consent', error);
      }
    }
  }

  /**
   * Check if user has given consent
   */
  hasConsent(): boolean {
    return this.consent() !== null;
  }

  /**
   * Check if user accepted cookies
   */
  hasAccepted(): boolean {
    return this.consent() === 'accepted';
  }

  /**
   * Reset consent (for testing)
   */
  reset(): void {
    this.consent.set(null);
    try {
      localStorage.removeItem(this.STORAGE_KEY);
    } catch (error) {
      console.error('Failed to reset cookie consent', error);
    }
  }
}
